/**
 * MarketShiftStats — animated bar comparing classic search vs. AI answers, with a
 * stat strip. Bespoke responsive SVG, i18n labels, role="img" + <title>.
 *
 * @spec /doc/specs/frontend/marketing-layout-charter.md
 * @userStory US-EP033-047
 * @ddl none
 */

'use client';

import { useTranslations } from 'next-intl';
import { MotionReveal } from '@/components/marketing/ui/motion-reveal';
import { cn } from '@/lib/utils';

export interface MarketShiftStatsProps {
  namespace?: string;
  className?: string;
}

const ROWS = ['row1', 'row2', 'row3'] as const;

export function MarketShiftStats({
  namespace = 'marketing.diagrams.marketShiftStats',
  className,
}: MarketShiftStatsProps) {
  const t = useTranslations(namespace);
  const titleId = 'market-shift-stats-title';
  return (
    <MotionReveal direction="up" className="min-w-0">
      <div className={cn('glass-card min-w-0 p-6 lg:p-8', className)}>
        <h3 className="text-display text-lg font-semibold text-white lg:text-xl">{t('title')}</h3>
        <svg
          viewBox="0 0 400 70"
          role="img"
          aria-labelledby={titleId}
          className="mt-4 w-full"
          preserveAspectRatio="xMidYMid meet"
        >
          <title id={titleId}>{t('title')}</title>
          {/* Track */}
          <rect x="0" y="26" width="400" height="18" rx="9" fill="#ffffff" opacity="0.06" />
          {/* AI answers share (≈60%) */}
          <rect x="0" y="26" width="240" height="18" rx="9" fill="url(#market-shift-grad)">
            <animate
              attributeName="width"
              from="0"
              to="240"
              dur="1s"
              fill="freeze"
              calcMode="spline"
              keySplines="0.22 0.61 0.36 1"
            />
          </rect>
          <text x="6" y="20" fill="#4eb6ec" fontSize="11" fontFamily="var(--font-inter)">
            {t('rows.row1.value')}
          </text>
          <text
            x="394"
            y="20"
            textAnchor="end"
            fill="#eeeeee"
            opacity="0.6"
            fontSize="11"
            fontFamily="var(--font-inter)"
          >
            {t('rows.row1.label')}
          </text>
          <defs>
            <linearGradient id="market-shift-grad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#1aabff" />
              <stop offset="100%" stopColor="#0d8edb" />
            </linearGradient>
          </defs>
        </svg>
        <dl className="mt-5 grid gap-3 sm:grid-cols-3">
          {ROWS.map((row) => (
            <div
              key={row}
              className="rounded-lg border border-white/5 bg-white/5 px-4 py-3"
            >
              <dt className="text-display text-2xl font-bold text-cyan-300">
                {t(`rows.${row}.value`)}
              </dt>
              <dd className="mt-0.5 text-sm font-medium text-white/80">{t(`rows.${row}.label`)}</dd>
              <dd className="mt-0.5 text-xs leading-relaxed text-white/50">
                {t(`rows.${row}.detail`)}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </MotionReveal>
  );
}
